import React, { Fragment, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import SEO from "../../components/seo";
import LayoutOne from "../../layouts/LayoutOne";
import NewsletterBar from "../../components/newdesign/NewsletterBar";
import { getDiscountPrice } from "../../helpers/product";
import { formatMoney } from "../../components/cart-redesign/pricing";
import LookupPanel from "../../components/return-redesign/LookupPanel";
import StateMessage from "../../components/return-redesign/StateMessage";
import SummaryBar from "../../components/return-redesign/SummaryBar";
import ReturnStepper from "../../components/return-redesign/ReturnStepper";
import ReturnActivityLog from "../../components/return-redesign/ReturnActivityLog";
import ReturnedItems from "../../components/return-redesign/ReturnedItems";
import EvidenceGallery from "../../components/return-redesign/EvidenceGallery";
import InfoPanel, { InfoRow } from "../../components/return-redesign/InfoPanel";
import {
  getStatusLabel,
  getStatusTone,
  parseReason,
} from "../../components/return-redesign/refundFlow";

const SUPPORT_EMAIL = "info@cocgreeninternational.co.uk";
const SUPPORT_PHONE = "+44(0)20 3371 178";

function ReturnStatus() {
  const [refunds, setRefunds] = useState([]);
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const currency = useSelector((state) => state.currency);
  const { products } = useSelector((state) => state.product);

  const baseURL = process.env.REACT_APP_BACKEND_URL;

  /*
   * There is no get-one-refund route, so the page reads the existing
   * `/refund/allrefunds` list and matches the reference the customer typed —
   * either their return ID or the order ID it was raised against.
   */
  const lookup = async (value) => {
    if (!value) return;

    setLoading(true);
    setSearched(true);
    setLookupError("");
    setReference(value);
    try {
      const response = await axios.get(`${baseURL}/refund/allrefunds`);
      const all = Array.isArray(response.data) ? response.data : [];
      const matches = all.filter(
        (refund) =>
          String(refund._id) === value ||
          String(refund.orderId?._id || refund.orderId) === value
      );

      setRefunds(matches);
      if (!matches.length) {
        setLookupError(
          "No return request matches that reference. Check the return ID from your confirmation, or enter the order ID it was raised against."
        );
      }
    } catch (error) {
      console.error("Error fetching returns:", error);
      setRefunds([]);
      setLookupError(
        error?.response?.data?.message ||
          "We couldn't load your return right now. Please try again in a moment."
      );
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (value) => formatMoney(currency, value);

  /*
   * A refund populates only its product's `name`, so the image and unit
   * price come from the catalogue already held in the store.
   */
  const buildLine = (refund, quantity) => {
    const productId = refund.ProductId?._id || refund.ProductId;
    const full = products.find((candidate) => candidate._id === productId);
    const rate = currency.currencyRate || 1;
    const base = full?.price?.basePrice;
    const discounted = base != null ? getDiscountPrice(base, full.discount) : null;
    const unit = base == null ? null : +((discounted ?? base) * rate).toFixed(2);

    return {
      id: String(productId || refund._id),
      name: refund.ProductId?.name || full?.name || "Product",
      quantity: quantity || 1,
      image: full?.mainImage,
      lineTotal: unit == null ? null : +(unit * (quantity || 1)).toFixed(2),
    };
  };

  const cards = useMemo(
    () =>
      refunds.map((refund) => {
        const parsed = parseReason(refund.reason);
        const order = refund.orderId && typeof refund.orderId === "object" ? refund.orderId : null;

        return {
          refund,
          parsed,
          order,
          line: buildLine(refund, parsed.quantity?.returning),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refunds, products, currency.currencyRate]
  );

  const results = () => {
    if (loading) {
      return (
        <StateMessage
          tone="loading"
          title="Looking up your return…"
          text="This only takes a moment."
        />
      );
    }

    if (lookupError) {
      return (
        <StateMessage tone="error" title="We couldn't find that return" text={lookupError}>
          <div className="c4rtn__actions">
            <Link
              className="c4rtn__btn c4rtn__btn--outline"
              to={process.env.PUBLIC_URL + "/refund"}
            >
              Start A Return
            </Link>
            <Link
              className="c4rtn__btn c4rtn__btn--outline"
              to={process.env.PUBLIC_URL + "/contact"}
            >
              Contact Support
            </Link>
          </div>
        </StateMessage>
      );
    }

    if (!searched) {
      return (
        <StateMessage
          title="Nothing to show yet"
          text="Enter your return ID — or the order ID it was raised against — to see where the request has got to."
        />
      );
    }

    if (!cards.length) return null;

    return (
      <div className="c4rtn__results">
        {cards.map(({ refund, parsed, order, line }) => {
          const submittedAt = refund.createdAt ? new Date(refund.createdAt) : null;
          const orderNumber =
            order?.customFullOrderId ||
            (order?.customOrderId ? `#${order.customOrderId}` : order?._id);

          return (
            <div className="c4rtn__return" key={refund._id}>
              <SummaryBar
                eyebrow="Return request"
                reference={`#${refund._id}`}
                meta={[
                  submittedAt
                    ? `Submitted ${submittedAt.toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}`
                    : null,
                  line.name,
                  refund.additionalImages?.length
                    ? `${refund.additionalImages.length} photo${
                        refund.additionalImages.length === 1 ? "" : "s"
                      }`
                    : null,
                ]}
                badge={getStatusLabel(refund.status)}
                tone={getStatusTone(refund.status)}
              />

              <ReturnStepper status={refund.status} />

              <div className="c4rtn__panels">
                <InfoPanel title="Return Reason">
                  <InfoRow label="Reason" value={parsed.summary || refund.reason} />
                  <InfoRow
                    label="Quantity"
                    value={
                      parsed.quantity
                        ? `${parsed.quantity.returning} of ${parsed.quantity.ordered}`
                        : null
                    }
                  />
                  <InfoRow label="Comments" value={parsed.comments} />
                  <InfoRow
                    label="Evidence"
                    value={
                      refund.additionalImages?.length
                        ? `${refund.additionalImages.length} photo${
                            refund.additionalImages.length === 1 ? "" : "s"
                          } supplied`
                        : "None supplied"
                    }
                  />
                </InfoPanel>

                <InfoPanel
                  title="Original Order"
                  footer={
                    order?._id ? (
                      <Link
                        className="c4rtn__btn c4rtn__btn--outline c4rtn__btn--block"
                        to={process.env.PUBLIC_URL + "/order-status"}
                      >
                        Track This Order
                      </Link>
                    ) : null
                  }
                >
                  <InfoRow label="Order" value={orderNumber} />
                  <InfoRow
                    label="Customer"
                    value={[
                      order?.billingInfo?.firstName,
                      order?.billingInfo?.lastName,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  />
                  <InfoRow label="Email" value={order?.billingInfo?.email} />
                  <InfoRow
                    label="Total"
                    value={
                      order?.overallTotal != null
                        ? formatPrice(order.overallTotal)
                        : null
                    }
                  />
                </InfoPanel>

                <InfoPanel
                  title="Refund Details"
                  footer={
                    <Link
                      className="c4rtn__btn c4rtn__btn--outline c4rtn__btn--block"
                      to={process.env.PUBLIC_URL + "/contact"}
                    >
                      Contact Support
                    </Link>
                  }
                >
                  <InfoRow label="Outcome" value={getStatusLabel(refund.status)} />
                  <InfoRow label="Preferred" value={parsed.resolution} />
                  <InfoRow label="Method" value="Original payment method" />
                  <InfoRow
                    label="Email"
                    value={<a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>}
                  />
                  <InfoRow
                    label="Phone"
                    value={
                      <a href={`tel:${SUPPORT_PHONE.replace(/[^\d+]/g, "")}`}>
                        {SUPPORT_PHONE}
                      </a>
                    }
                  />
                </InfoPanel>
              </div>

              <ReturnedItems lines={[line]} formatPrice={formatPrice} />

              {refund.additionalImages?.length ? (
                <EvidenceGallery images={refund.additionalImages} />
              ) : null}

              <ReturnActivityLog
                status={refund.status}
                createdAt={refund.createdAt}
                productName={line.name}
                imageCount={refund.additionalImages?.length || 0}
              />
            </div>
          );
        })}

        <div className="c4rtn__footer-actions">
          <Link
            className="c4rtn__btn c4rtn__btn--primary"
            to={process.env.PUBLIC_URL + "/my-account"}
          >
            Back To Orders
          </Link>
          <Link
            className="c4rtn__btn c4rtn__btn--outline"
            to={process.env.PUBLIC_URL + "/refund"}
          >
            Start Another Return
          </Link>
          <Link
            className="c4rtn__btn c4rtn__btn--outline"
            to={process.env.PUBLIC_URL + "/contact"}
          >
            Contact Support
          </Link>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <SEO
        titleTemplate="Return Status"
        description="Follow the progress of your Cey4Hub return request."
      />

      <LayoutOne headerTop="visible">
        <main className="c4rtn">
          <div className="c4wide">
            <nav className="c4rtn__crumbs" aria-label="Breadcrumb">
              <Link to={process.env.PUBLIC_URL + "/"}>Home</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Return status</span>
            </nav>

            <LookupPanel
              id="c4rtn-status-lookup"
              title="Track Your Return"
              subtitle="See where your return request has got to and what happens next."
              label="Return or order ID"
              placeholder="e.g. 6721f0c3b4d3e07a1c8f4b21"
              help="Your return ID is shown when you submit a request and in the confirmation email. The order ID works too."
              action="Track Return"
              loadingAction="Searching…"
              isLoading={loading}
              initialValue={reference}
              onSubmit={lookup}
            />

            {results()}
          </div>
        </main>

        <NewsletterBar />
      </LayoutOne>
    </Fragment>
  );
}

export default ReturnStatus;
